import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { FormProvider, useNotificationContext } from 'core/states';
import { useCorporatePartPriceActions } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartPriceListItem } from 'corporate/types';

import { EndActivePriceConfirmation } from './EndActivePriceConfirmation';
import {
  getDefaultValues,
  mapActiveOverlappingPricePayload,
  mapUpsertCorporatePartPrice,
} from './mappers';
import { upsertCorporatePartPriceSchema } from './schema';
import { IUpsertCorporatePartPriceForm } from './types';
import { UpsertCorporatePartPriceFormFields } from './UpsertCorporatePartPriceFormFields';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  price?: ICorporatePartPriceListItem;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertCorporatePartPrice = ({
  corporatePart,
  price,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const [removedItems, setRemovedItems] = useState<number[]>([]);
  const [pendingFormData, setPendingFormData] = useState<IUpsertCorporatePartPriceForm | null>(
    null
  );
  const [pendingReset, setPendingReset] =
    useState<UseFormReset<IUpsertCorporatePartPriceForm> | null>(null);
  const [isEndActivePriceOpen, setIsEndActivePriceOpen] = useState(false);

  const { clearNotification } = useNotificationContext();
  const { upsertCorporatePartPrice, checkActiveOverlappingPrice } = useCorporatePartPriceActions();

  const defaultValues = useMemo(
    () => getDefaultValues(corporatePart, price),
    [corporatePart, price]
  );

  const handleOnClose = (reset?: UseFormReset<IUpsertCorporatePartPriceForm>) => {
    clearNotification();
    setRemovedItems([]);
    setPendingFormData(null);
    setPendingReset(null);
    setIsEndActivePriceOpen(false);
    onClose();
    reset?.(defaultValues);
  };

  const submitUpsert = async (
    data: IUpsertCorporatePartPriceForm,
    reset: UseFormReset<IUpsertCorporatePartPriceForm>
  ) => {
    const payload = mapUpsertCorporatePartPrice(data, corporatePart, price, removedItems);

    const { success } = await upsertCorporatePartPrice(payload);

    if (!success) {
      return;
    }

    onSubmitSuccess?.();

    setTimeout(() => handleOnClose(reset), 1000);
  };

  const onSubmit =
    (reset: UseFormReset<IUpsertCorporatePartPriceForm>) =>
    async (data: IUpsertCorporatePartPriceForm) => {
      const overlapPayload = mapActiveOverlappingPricePayload(data, corporatePart, price);
      const { record: activeCorporatePartPrice } =
        await checkActiveOverlappingPrice(overlapPayload);

      if (activeCorporatePartPrice?.id) {
        setPendingFormData(data);
        setPendingReset(() => reset);
        setIsEndActivePriceOpen(true);

        return;
      }

      await submitUpsert(data, reset);
    };

  const onConfirmEndActive = async () => {
    if (!pendingFormData || !pendingReset) {
      return;
    }

    setIsEndActivePriceOpen(false);

    await submitUpsert(pendingFormData, pendingReset);
  };

  const onCancelEndActive = () => {
    setIsEndActivePriceOpen(false);
    setPendingFormData(null);
    setPendingReset(null);
  };

  const handleRemoveSuccess = () => {
    onRemoveSuccess?.();
    setTimeout(() => handleOnClose(), 1000);
  };

  return (
    <FormProvider
      schema={upsertCorporatePartPriceSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertCorporatePartPriceFormFields
        corporatePart={corporatePart}
        price={price}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        onItemRemoved={(itemId) => setRemovedItems((prev) => [...prev, itemId])}
        onRemoveSuccess={handleRemoveSuccess}
      />

      <EndActivePriceConfirmation
        isOpen={isEndActivePriceOpen}
        onConfirm={onConfirmEndActive}
        onCancel={onCancelEndActive}
      />
    </FormProvider>
  );
};
